import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../components/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';

const { FiLock, FiMail, FiCheckCircle, FiAlertCircle } = FiIcons;

const EmergencyPasswordReset = () => {
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [method, setMethod] = useState('login');

  const handleLoginAndChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const { data: loginData, error: loginError } = await supabase.auth.signInWithPassword({
        email: email,
        password: currentPassword,
      });

      if (loginError) {
        throw new Error('Unable to sign in with this password. Please check your current password.');
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        throw updateError;
      }

      setSuccess('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'Error changing password');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailReset = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        throw error;
      }

      setSuccess('Reset email sent! Check your inbox.');
    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'Error sending reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md p-8 space-y-6 bg-white/10 backdrop-blur-md rounded-2xl shadow-2xl border border-white/20"
      >
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-100">
            Emergency Password Reset
          </h1>
          <p className="mt-2 text-gray-400">
            For: {email || 'Enter your email'}
          </p>
        </div>

        <div className="flex space-x-2 p-1 bg-gray-800 rounded-lg">
          <button
            onClick={() => setMethod('login')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              method === 'login'
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Login + Change
          </button>
          <button
            onClick={() => setMethod('admin')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              method === 'admin'
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Email Reset
          </button>
        </div>

        {error && (
          <div className="flex items-center p-3 space-x-2 text-sm text-red-200 bg-red-500/20 rounded-lg">
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5" />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="flex items-center p-3 space-x-2 text-sm text-green-200 bg-green-500/20 rounded-lg">
            <SafeIcon icon={FiCheckCircle} className="w-5 h-5" />
            <span>{success}</span>
          </div>
        )}

        {method === 'login' && (
          <form onSubmit={handleLoginAndChangePassword} className="space-y-4">
            <div className="relative">
              <SafeIcon icon={FiMail} className="absolute w-5 h-5 text-gray-400 top-3.5 left-4" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="w-full py-3 pl-12 pr-4 text-gray-200 bg-gray-900/50 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all duration-300"
                required
              />
            </div>

            <div className="relative">
              <SafeIcon icon={FiLock} className="absolute w-5 h-5 text-gray-400 top-3.5 left-4" />
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Current Password"
                className="w-full py-3 pl-12 pr-4 text-gray-200 bg-gray-900/50 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all duration-300"
                required
              />
            </div>

            <div className="relative">
              <SafeIcon icon={FiLock} className="absolute w-5 h-5 text-gray-400 top-3.5 left-4" />
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New Password"
                className="w-full py-3 pl-12 pr-4 text-gray-200 bg-gray-900/50 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all duration-300"
                required
                minLength={6}
              />
            </div>

            <div className="relative">
              <SafeIcon icon={FiLock} className="absolute w-5 h-5 text-gray-400 top-3.5 left-4" />
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm New Password"
                className="w-full py-3 pl-12 pr-4 text-gray-200 bg-gray-900/50 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all duration-300"
                required
                minLength={6}
              />
            </div>

            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <p className="text-red-400 text-sm">Passwords do not match</p>
            )}

            <button
              type="submit"
              disabled={loading || newPassword !== confirmPassword || !newPassword || !currentPassword}
              className="w-full flex justify-center items-center py-3 px-4 text-lg font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-blue-500 transition-all duration-300"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              ) : null}
              {loading ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        )}

        {method === 'admin' && (
          <form onSubmit={handleEmailReset} className="space-y-4">
            <div className="relative">
              <SafeIcon icon={FiMail} className="absolute w-5 h-5 text-gray-400 top-3.5 left-4" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="w-full py-3 pl-12 pr-4 text-gray-200 bg-gray-900/50 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all duration-300"
                required
              />
            </div>

            <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4">
              <p className="text-yellow-200 text-sm">
                <strong>Important instructions:</strong>
                <br />
                1. An email will be sent to your address
                <br />
                2. The link may point to localhost:3000
                <br />
                3. <strong>Replace</strong> "localhost:3000" with your Netlify URL in the link
                <br />
                4. Then click the modified link
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center py-3 px-4 text-lg font-semibold text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:bg-orange-800 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-orange-500 transition-all duration-300"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              ) : null}
              {loading ? 'Sending...' : 'Send Reset Email'}
            </button>
          </form>
        )}

        <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4">
          <p className="text-blue-200 text-sm">
            <strong>Tip:</strong> If you don't remember your current password, use the "Email Reset" method and follow the instructions to modify the link.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default EmergencyPasswordReset;